import { Check, Copy, Puzzle, RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Button, Card, CardContent, CardHeader, Input } from '@/components/ui';
import { useCurrentUser } from '@/lib/auth/AuthProvider';
import { cn } from '@/lib/cn';
import type { ExternalClientTier, UserRole } from '@/types/auth';

const ALL_ROLES: UserRole[] = [
  'data_analyst_standard',
  'data_analyst_domain_expert',
  'internal_front_office',
  'administrator',
  'external_client',
];

const TIERS: (ExternalClientTier | '')[] = ['', 'free', 'analyst', 'enterprise'];

/**
 * Studio /extension — token generator for the ETT Quick Add browser
 * extension. The local backend (`APP_ENV=dev`) honours an `X-Test-User`
 * JSON header; this page composes a copy-ready payload + the full
 * extension settings snippet so analysts don't have to hand-write the
 * JSON or memorise the role names.
 */
export function ExtensionSetup() {
  const { t } = useTranslation(['studio', 'common']);
  const me = useCurrentUser();

  // Seed from the logged-in user when available; the analyst can still
  // override anything before copying (e.g. add admin to test elevated
  // flows without re-logging).
  const [userId, setUserId] = useState<string>(
    () => me?.id ?? '00000000-0000-0000-0000-000000000001',
  );
  const [roles, setRoles] = useState<Set<UserRole>>(
    () =>
      new Set<UserRole>(
        me?.roles && me.roles.length > 0
          ? me.roles
          : ['data_analyst_standard', 'data_analyst_domain_expert'],
      ),
  );
  const [tier, setTier] = useState<string>(me?.tier ?? '');
  const [backendUrl, setBackendUrl] = useState<string>(() => deriveBackend());

  const payload = useMemo(
    () => ({
      id: userId,
      roles: ALL_ROLES.filter((r) => roles.has(r)),
      tier: tier ? tier : null,
    }),
    [userId, roles, tier],
  );

  const isExternal = roles.has('external_client');

  return (
    <div className="space-y-4 p-6">
      <header>
        <div className="flex items-center gap-2">
          <Puzzle className="h-5 w-5 text-accent" aria-hidden />
          <h1 className="text-display text-brand-900">
            {t('studio:extension.title', 'Browser extension setup')}
          </h1>
        </div>
        <p className="mt-1 text-body text-slate-600">
          {t(
            'studio:extension.subtitle',
            'Generate the auth payload to paste into the ETT Quick Add extension. Works against the local backend; for production use the Bearer-token instructions below.',
          )}
        </p>
      </header>

      <Card>
        <CardHeader>
          <h2 className="text-h2 text-brand-900">
            {t('studio:extension.dev.title', 'Dev / staging — X-Test-User')}
          </h2>
          <p className="mt-1 text-small text-slate-600">
            {t(
              'studio:extension.dev.hint',
              'Honoured only when the backend is in APP_ENV=dev or test. Bypasses Keycloak, useful for local development.',
            )}
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field
              label={t('studio:extension.field.userId', 'User ID (UUID, audit attribution)')}
            >
              <div className="flex gap-1">
                <Input value={userId} onChange={(e) => setUserId(e.target.value)} />
                <Button
                  variant="ghost"
                  size="icon"
                  type="button"
                  onClick={() => setUserId(uuid4())}
                  title={t('studio:extension.field.regenUuid', 'Generate a fresh UUID')}
                >
                  <RefreshCw className="h-3.5 w-3.5" aria-hidden />
                </Button>
              </div>
            </Field>
            <Field label={t('studio:extension.field.tier', 'Tier (external clients only)')}>
              <select
                value={tier}
                onChange={(e) => setTier(e.target.value)}
                disabled={!isExternal}
                className={cn(
                  'h-9 w-full rounded border border-slate-300 px-2 text-small',
                  !isExternal && 'cursor-not-allowed bg-slate-50 text-slate-400',
                )}
              >
                {TIERS.map((tv) => (
                  <option key={tv || 'none'} value={tv}>
                    {tv || '(none)'}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field label={t('studio:extension.field.roles', 'Roles')}>
            <div className="flex flex-wrap gap-1.5">
              {ALL_ROLES.map((r) => {
                const on = roles.has(r);
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => {
                      setRoles((prev) => {
                        const next = new Set(prev);
                        if (next.has(r)) next.delete(r);
                        else next.add(r);
                        return next;
                      });
                    }}
                    className={cn(
                      'rounded-full px-2.5 py-0.5 text-small transition',
                      on
                        ? 'bg-brand-100 text-brand-900'
                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200',
                    )}
                  >
                    {r}
                  </button>
                );
              })}
            </div>
          </Field>

          <CopyBlock
            label={t('studio:extension.json.label', 'X-Test-User payload')}
            value={JSON.stringify(payload)}
          />

          <details className="mt-2">
            <summary className="cursor-pointer text-small text-slate-500">
              {t('studio:extension.json.pretty', 'Show pretty-printed')}
            </summary>
            <pre className="mt-1 overflow-x-auto rounded bg-slate-50 p-2 font-mono text-small text-slate-700">
              {JSON.stringify(payload, null, 2)}
            </pre>
          </details>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="text-h2 text-brand-900">
            {t('studio:extension.config.title', 'Full extension config')}
          </h2>
          <p className="mt-1 text-small text-slate-600">
            {t(
              'studio:extension.config.hint',
              'Paste each value into the extension settings (click the toolbar icon → ⚙ Settings).',
            )}
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <Field label={t('studio:extension.field.backend', 'Backend URL')}>
            <Input
              value={backendUrl}
              onChange={(e) => setBackendUrl(e.target.value)}
              placeholder="http://localhost:8000"
            />
          </Field>
          <CopyBlock
            label={t('studio:extension.config.backend', '→ Backend URL field')}
            value={backendUrl}
          />
          <CopyBlock
            label={t('studio:extension.config.testUser', '→ Dev test-user header field')}
            value={JSON.stringify(payload)}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <h2 className="text-h2 text-brand-900">
              {t('studio:extension.prod.title', 'Production — Bearer token')}
            </h2>
            <Badge variant="warning">
              {t('studio:extension.prod.badge', 'Keycloak required')}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-2 text-body text-slate-700">
          <p>
            {t(
              'studio:extension.prod.intro',
              'When the backend runs in APP_ENV=prod the X-Test-User header is ignored — every request must carry a Keycloak-issued JWT.',
            )}
          </p>
          <ol className="ml-5 list-decimal space-y-1 text-small">
            <li>
              {t(
                'studio:extension.prod.step1',
                'Log in to the Studio normally (Keycloak SSO).',
              )}
            </li>
            <li>
              {t(
                'studio:extension.prod.step2',
                'Open DevTools → Application → Local Storage → the Keycloak key — copy `access_token`.',
              )}
            </li>
            <li>
              {t(
                'studio:extension.prod.step3',
                'In the extension settings, leave Test-user blank and paste the JWT into the Bearer token field.',
              )}
            </li>
            <li>
              {t(
                'studio:extension.prod.step4',
                'Refresh when the token expires (default 5 min) — Keycloak silent-refresh runs in this tab whenever the Studio is open.',
              )}
            </li>
          </ol>
          <p className="text-small text-slate-500">
            {t(
              'studio:extension.prod.future',
              'A proper long-lived personal-access-token endpoint is on the roadmap; in the meantime the short-lived JWT is the right call for shared instances.',
            )}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── helpers ───────────────────────────────────────────────────

function Field({
  label,
  children,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-0.5 block text-h4 text-slate-700">{label}</span>
      {children}
    </label>
  );
}

function CopyBlock({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(id);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // Fallback for older browsers / non-secure contexts.
      const ta = document.createElement('textarea');
      ta.value = value;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        setCopied(true);
      } finally {
        document.body.removeChild(ta);
      }
    }
  };

  return (
    <div>
      <div className="mb-0.5 flex items-center justify-between gap-2">
        <span className="text-h4 text-slate-700">{label}</span>
        <Button size="sm" variant="ghost" onClick={copy} type="button">
          {copied ? (
            <>
              <Check className="mr-1 h-3.5 w-3.5 text-green-600" aria-hidden />
              Copied
            </>
          ) : (
            <>
              <Copy className="mr-1 h-3.5 w-3.5" aria-hidden />
              Copy
            </>
          )}
        </Button>
      </div>
      <code className="block break-all rounded border border-slate-200 bg-slate-50 px-2 py-1.5 font-mono text-small text-slate-800">
        {value}
      </code>
    </div>
  );
}

function uuid4(): string {
  // crypto.randomUUID is available in every browser the Studio targets.
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  // Fallback — not cryptographically strong but the dev test-user
  // payload doesn't need it.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function deriveBackend(): string {
  // Studio runs on :3000 in dev, backend on :8000 — same host either
  // way. In prod the Studio is served from the same origin as the
  // backend so we can default to window.location.origin.
  if (typeof window === 'undefined') return 'http://localhost:8000';
  const { protocol, hostname, port } = window.location;
  if (port === '3000') return `${protocol}//${hostname}:8000`;
  return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
}
